import React, { useEffect, useState } from "react";
import { Button, InputNumber, message, Tooltip, Modal } from "antd";
import { Footer, Sidebar } from "../../../../components";
import Dropdowns from "./Dropdowns";
import dayjs from "dayjs";
import {
  fetchVarietyData,
  UpdateVarietyHarvestedData,
  copyPreviousVarietyHarvestedData,
} from "../../../../utils/api";
import { dummyTableData as strawberryDummyTableData } from "./dummyDataStrawberry";

import {
  getSiteForecastConfig,
  getTotalYield,
  isWeekInRange,
  weekToNumber,
} from "./utils";

import AnalysisTable from "./AnalysisTable";
import HarvestTable from "./HarvestTable";
import RipenessChart from "./RipenessChart";
import { BACKEND_DATE_FORMAT } from "../../../../consts";

function stableRandomFourDigit(...keys) {
  // Join all identifying info into a single string
  const str = keys.join("-");

  // Simple 32-bit additive hash
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // force into 32-bit integer
  }

  // We want a value in [3000, 5000], i.e. 2001 possible values
  const range = 5000 - 3000 + 1; // 2001
  const hashedVal = Math.abs(hash) % range;
  return hashedVal + 3000;
}

const initialAnalysisTableData = {};

const TomatoGrowerDashboard = ({
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  siteData,
  setSiteData,
  currentSite,
  setCurrentSite,
  siteLoading,
  setSiteLoading,
  currentCrop,
  setCurrentCrop,
  cropLoading,
  setCropLoading,
  cropData,
  setCropData,
  searchParams,
  setSearchParams,
  settingDefaults,
  setSettingDefaults,
}) => {
  // Decide which "dummy" data is used for harvest table
  const dummyData =
    currentCrop?.name === "tomato" ? {} : strawberryDummyTableData;

  // HARVEST states
  const [tableData, setTableData] = useState(dummyData);
  const [displayData, setDisplayData] = useState({});
  const [dataSource, setDataSource] = useState([]);

  // ANALYSIS states
  const [analysisTableData, setAnalysisTableData] = useState(
    initialAnalysisTableData,
  );
  const [displayAnalysisData, setDisplayAnalysisData] = useState({});
  const [analysisDataSource, setAnalysisDataSource] = useState([]);
  const [varietyLoadingMap, setVarietyLoadingMap] = useState({});
  const [expandedKeys, setExpandedKeys] = useState([]);

  // Shared states
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteMetadata, setSiteMetadata] = useState({});
  const [currentGreenhouse, setCurrentGreenhouse] = useState([]);
  const [greenhouseData, setGreenhouseData] = useState([]);
  const [currentVariety, setCurrentVariety] = useState([]);
  const [varietyData, setVarietyData] = useState([]);
  const [currentDate, setCurrentDate] = useState(null);
  const [fromWeek, setFromWeek] = useState(null);
  const [toWeek, setToWeek] = useState(null);
  const [forcastDate, setForcastDate] = useState(null);
  const [tableHeaders, setTableHeaders] = useState([]);
  const [dateHeaders, setDateHeaders] = useState([]);
  const [weekTimeline, setWeekTimeline] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [currentMetric, setCurrentMetric] = useState(null);
  const [metricType, setMetricType] = useState(null);
  const [metricData, setMetricData] = useState([]);
  const [showMetricColumn, setShowMetricColumn] = useState(true);

  // Harvest schedule metrics
  const [harvestScheduleMetrics, setHarvestScheduleMetrics] = useState([]);
  const [
    harvestScheduleMetricsDefaultValues,
    setHarvestScheduleMetricsDefaultValues,
  ] = useState({});

  // State for Ripeness Chart modal
  const [ripenessModalOpen, setRipenessModalOpen] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  // For checking if current crop is "berry"
  const isBerry =
    currentCrop?.name === "strawberry" || currentCrop?.name === "blueberry";

  // ----------------------------------------------------------------
  // Disable date outside from/to
  // ----------------------------------------------------------------
  const disabledDate = (cur) => {
    if (!fromWeek || !toWeek || !cur) return true;
    const cy = cur.isoWeekYear();
    const cw = cur.isoWeek();
    return !isWeekInRange(cy, cw, fromWeek, toWeek);
  };

  function isVarietyPresentInGreenhouse(list, object) {
    return list.some((item) => item.id === object.id);
  }

  function updateEachVarietyWeek() {
    const timeline = greenhouseData.reduce((acc, gh) => {
      acc[gh.name] = gh.varieties.reduce((varietyTimeline, variety) => {
        varietyTimeline[variety.name] = {
          FROM_WEEK: gh.metadata?.[variety.name]?.FROM_WEEK ?? null,
          FROM_YEAR: gh.metadata?.[variety.name]?.FROM_YEAR ?? null,
          TO_WEEK: gh.metadata?.[variety.name]?.TO_WEEK ?? null,
          TO_YEAR: gh.metadata?.[variety.name]?.TO_YEAR ?? null,
        };
        return varietyTimeline;
      }, {});
      return acc;
    }, {});
    setWeekTimeline(timeline);
  }

  // ----------------------------------------------------------------
  // Build tableHeaders from greenhouse/variety
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!siteMetadata || !currentSite || !currentGreenhouse || !currentVariety)
      return;
    const headers = [];
    for (let gh of currentGreenhouse) {
      for (let p of currentVariety) {
        if (isVarietyPresentInGreenhouse(gh.varieties, p)) {
          headers.push({
            site: currentSite.displayName,
            greenhouse: gh.name,
            variety: p.name,
          });
        }
      }
    }
    setTableHeaders(headers);
  }, [siteMetadata, currentSite, currentGreenhouse, currentVariety]);

  // ----------------------------------------------------------------
  // Set from/to weeks & pick default currentDate
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!siteMetadata || !Object.keys(siteMetadata).length) {
      if (Object.keys(siteMetadata).length === 0) {
        setErrorMessage("Metadata not Configured!");
      }
      return;
    }
    setErrorMessage(null);
    setInfoMessage(null);

    // Capture Harvest Schedule config from site metadata
    if (siteMetadata?.forecast?.HARVEST_SCHEDULE_METRICS) {
      setHarvestScheduleMetrics(
        siteMetadata?.forecast?.HARVEST_SCHEDULE_METRICS,
      );
    }
    if (siteMetadata?.forecast?.HARVEST_SCHEDULE_METRICS_DEFAULT_VALUE) {
      setHarvestScheduleMetricsDefaultValues(
        siteMetadata?.forecast?.HARVEST_SCHEDULE_METRICS_DEFAULT_VALUE,
      );
    }

    // FROM / TO
    if (
      !siteMetadata.FROM_WEEK ||
      !siteMetadata.FROM_YEAR ||
      !siteMetadata.TO_WEEK ||
      !siteMetadata.TO_YEAR
    ) {
      setErrorMessage("FROM and TO weeks not set in site metadata!");
      setFromWeek(null);
      setToWeek(null);
      setCurrentDate(null);
      return;
    }

    const userFromWeek = {
      YEAR: siteMetadata.FROM_YEAR,
      WEEK: siteMetadata.FROM_WEEK,
    };
    const userToWeek = {
      YEAR: siteMetadata.TO_YEAR,
      WEEK: siteMetadata.TO_WEEK,
    };
    setFromWeek(userFromWeek);
    setToWeek(userToWeek);

    const now = dayjs();
    const cYear = now.isoWeekYear();
    const cWeek = now.isoWeek();
    let defaultYear = userFromWeek.YEAR;
    let defaultWeek = userFromWeek.WEEK;

    if (isWeekInRange(cYear, cWeek, userFromWeek, userToWeek)) {
      defaultYear = cYear;
      defaultWeek = cWeek;
    } else {
      const cVal = weekToNumber(cYear, cWeek);
      const fVal = weekToNumber(userFromWeek.YEAR, userFromWeek.WEEK);
      const tVal = weekToNumber(userToWeek.YEAR, userToWeek.WEEK);

      if (cVal > tVal) {
        setInfoMessage("Harvest is already completed for this season.");
        defaultYear = userToWeek.YEAR;
        defaultWeek = userToWeek.WEEK;
      } else if (cVal < fVal) {
        setErrorMessage(
          `Planting not started yet. It will start from Week ${userFromWeek.WEEK} of Year ${userFromWeek.YEAR}`,
        );
      }
    }

    const defaultDate = dayjs()
      .year(defaultYear)
      .isoWeek(defaultWeek)
      .startOf("isoWeek");
    setCurrentDate(defaultDate.format("YYYY-MM-DD"));
    updateEachVarietyWeek();
  }, [siteMetadata]);

  // ----------------------------------------------------------------
  // Set forcastDate from currentDate
  // ----------------------------------------------------------------
  useEffect(() => {
    if (currentDate) {
      setForcastDate(dayjs(currentDate).format("YYYY-MM-DD"));
    }
  }, [currentDate]);

  // ----------------------------------------------------------------
  // Build 7-day dateHeaders
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!forcastDate) return;
    const headers = [];
    for (let i = 0; i < 7; i++) {
      headers.push(dayjs(forcastDate).add(i, "day").format("M/D/YYYY"));
    }
    setDateHeaders(headers);
  }, [forcastDate]);

  const fetchAllData = async () => {
    if (!dateHeaders.length || !tableHeaders.length || !currentSite?.id) return;

    // 1. Mark all relevant variety combos as "loading"
    const newVarLoading = {};
    const varietyKeySet = new Set();
    tableHeaders.forEach(({ site, variety }) => {
      varietyKeySet.add(`${site}-${variety}`);
    });
    varietyKeySet.forEach((vKey) => {
      newVarLoading[vKey] = true;
    });
    setVarietyLoadingMap((prev) => ({ ...prev, ...newVarLoading }));

    // 2. Gather greenhouse IDs, variety IDs, dates
    const greenhouseIds = currentGreenhouse.map((gh) => gh.id);
    const varietyIds = currentVariety.map((v) => v.id);
    const harvestDates = dateHeaders.map((d) =>
      dayjs(d).format(BACKEND_DATE_FORMAT),
    );
    try {
      const response = await fetchVarietyData(
        currentOrg,
        currentSite?.id,
        currentCrop?.id,
        greenhouseIds,
        varietyIds,
        harvestDates,
      );
      if (!response.data?.success) {
        console.error("fetchVarietyData error:", response.data?.message);
        return;
      }

      const bigData = response.data?.data || response.data || {};

      // Pull out the forecast keys from site metadata
      const {
        totalRows,
        areaPerRow,
        areaOfGH,
        averageYieldPerSqMeterKeyName,
        averageYieldKeyName,
        refinedAverageYieldKeyName,
      } = getSiteForecastConfig(currentSite, currentCrop?.name);

      // PART A: Build Analysis Table Data
      const partialDataCopy = {};
      for (const siteName in bigData) {
        if (!partialDataCopy[siteName]) {
          partialDataCopy[siteName] = {};
        }
        const siteDict = bigData[siteName] || {};
        for (const ghName in siteDict) {
          if (!partialDataCopy[siteName][ghName]) {
            partialDataCopy[siteName][ghName] = {};
          }
          const ghDict = siteDict[ghName] || {};
          for (const varietyName in ghDict) {
            if (!partialDataCopy[siteName][ghName][varietyName]) {
              partialDataCopy[siteName][ghName][varietyName] = {
                YieldByDate: {},
                RefinedByDate: {},
              };
            }
            const varDict = ghDict[varietyName] || {};
            for (const dateStr in varDict) {
              const forecastJSON = varDict[dateStr]?.forecast || null;
              // "Yield" (the "average" key)
              const yieldVal =
                getTotalYield(
                  forecastJSON,
                  averageYieldKeyName,
                  totalRows,
                  areaPerRow,
                  areaOfGH,
                  averageYieldPerSqMeterKeyName,
                ) || null;
              // "Refined"
              const refinedVal =
                getTotalYield(
                  forecastJSON,
                  refinedAverageYieldKeyName,
                  totalRows,
                  areaPerRow,
                  areaOfGH,
                  averageYieldPerSqMeterKeyName,
                ) || null;

              partialDataCopy[siteName][ghName][varietyName].YieldByDate[
                dateStr
              ] = yieldVal ?? null;
              partialDataCopy[siteName][ghName][varietyName].RefinedByDate[
                dateStr
              ] = refinedVal ?? null;
            }
          }
        }
      }

      // If some date is missing, store null
      dateHeaders.forEach((headerDate) => {
        const iso = dayjs(headerDate).format(BACKEND_DATE_FORMAT);
        tableHeaders.forEach(({ site, greenhouse, variety }) => {
          if (!partialDataCopy[site]) {
            partialDataCopy[site] = {};
          }
          if (!partialDataCopy[site][greenhouse]) {
            partialDataCopy[site][greenhouse] = {};
          }
          if (!partialDataCopy[site][greenhouse][variety]) {
            partialDataCopy[site][greenhouse][variety] = {
              YieldByDate: {},
              RefinedByDate: {},
            };
          }
          if (
            partialDataCopy[site][greenhouse][variety].YieldByDate[iso] ===
            undefined
          ) {
            partialDataCopy[site][greenhouse][variety].YieldByDate[iso] = null;
          }
          if (
            partialDataCopy[site][greenhouse][variety].RefinedByDate[iso] ===
            undefined
          ) {
            partialDataCopy[site][greenhouse][variety].RefinedByDate[iso] =
              null;
          }
        });
      });
      setAnalysisTableData(partialDataCopy);

      // PART B: Build Harvest Planner Table Data
      // Create a  map from dateStr (YYYY-MM-DD) -> day index
      setHarvestScheduleTable(bigData);

      // Mark each variety as loaded
      const loadedVarMap = {};
      varietyKeySet.forEach((vKey) => {
        loadedVarMap[vKey] = false;
      });
      setVarietyLoadingMap((prev) => ({ ...prev, ...loadedVarMap }));
    } catch (err) {
      console.error("fetchVarietyData exception:", err);
    }
  };

  // ----------------------------------------------------------------
  // MAIN DATA FETCH: Both Analysis & Harvest Data
  // ----------------------------------------------------------------
  useEffect(() => {
    fetchAllData();
  }, [
    dateHeaders,
    tableHeaders,
    currentSite,
    currentGreenhouse,
    currentVariety,
    currentCrop,
    isBerry,
  ]);

  // ----------------------------------------------------------------
  // Build Harvest Table (displayData) from local tableData
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!dateHeaders.length) return;

    const newDisplay = {};
    for (const siteKey in tableData) {
      newDisplay[siteKey] = {};
      for (const ghKey in tableData[siteKey]) {
        newDisplay[siteKey][ghKey] = {};
        for (const varietyKey in tableData[siteKey][ghKey]) {
          newDisplay[siteKey][ghKey][varietyKey] = {};
          const varietyObj = tableData[siteKey][ghKey][varietyKey];

          // varietyObj: { [metric]: [ arrayOf7Values ] }
          for (const metricKey in varietyObj) {
            const val = varietyObj[metricKey];
            if (Array.isArray(val) && val.length === 7) {
              newDisplay[siteKey][ghKey][varietyKey][metricKey] = [...val];
            } else if (Array.isArray(val) && val.length < 7) {
              const filled = [...val];
              while (filled.length < 7) {
                filled.push(filled[filled.length - 1] || 0);
              }
              newDisplay[siteKey][ghKey][varietyKey][metricKey] = filled;
            } else if (!Array.isArray(val)) {
              // If you got something unexpected, fallback to 7 of that
              newDisplay[siteKey][ghKey][varietyKey][metricKey] =
                Array(7).fill(val);
            } else {
              // If it's an array but length > 7 or invalid, fallback
              newDisplay[siteKey][ghKey][varietyKey][metricKey] =
                Array(7).fill(0);
            }
          }
        }
      }
    }
    setDisplayData(newDisplay);
  }, [tableData, dateHeaders]);

  // ----------------------------------------------------------------
  // Build harvest table's dataSource from displayData
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!tableHeaders.length || !displayData || !dateHeaders.length) return;
    const newDataSource = [];

    tableHeaders.forEach(({ site, greenhouse, variety }) => {
      if (!displayData[site]?.[greenhouse]?.[variety]) return;
      const varietyObj = displayData[site][greenhouse][variety];
      const metricKeys = Object.keys(varietyObj);
      if (!metricKeys.length) return;

      const FROM_WEEK = weekTimeline[greenhouse]?.[variety]?.FROM_WEEK ?? null;
      const FROM_YEAR = weekTimeline[greenhouse]?.[variety]?.FROM_YEAR ?? null;
      const TO_WEEK = weekTimeline[greenhouse]?.[variety]?.TO_WEEK ?? null;
      const TO_YEAR = weekTimeline[greenhouse]?.[variety]?.TO_YEAR ?? null;

      // Calculate min and max values from ripeness chart data
      const ripenessData = siteMetadata?.ripeness_chart?.[variety] || [];
      const harvestCriteria =
        siteMetadata?.forecast?.RIPENESS_CRITERIA_METRIC_NAME ||
        "Harvest Criteria";
      const minValue =
        ripenessData.length > 0
          ? Math.min(...ripenessData.map((item) => item.label))
          : 0;
      const maxValue =
        ripenessData.length > 0
          ? Math.max(...ripenessData.map((item) => item.label))
          : undefined;

      const parentRow = {
        key: `${site}-${greenhouse}-${variety}-parent`,
        isParent: true,
        subsite: `${site} - ${greenhouse} ${variety}`,
        detail: "",
        children: metricKeys.map((mKey, idx) => {
          const dayValues = varietyObj[mKey];
          const childRow = {
            key: `${site}-${greenhouse}-${variety}-${mKey}`,
            isParent: false,
            metricKey: mKey,
            site,
            greenhouse,
            variety,
            minValue: mKey === harvestCriteria ? minValue : 0,
            maxValue: mKey === harvestCriteria ? maxValue : undefined,
          };
          if (idx === 0) {
            childRow.subsite = `W ${FROM_WEEK},${FROM_YEAR} - ${TO_WEEK},${TO_YEAR}`;
            childRow.detail = mKey;
          } else {
            childRow.subsite = "";
            childRow.detail = mKey;
          }
          dayValues.forEach((val, i) => {
            childRow[`day${i}`] = val;
          });
          return childRow;
        }),
      };
      newDataSource.push(parentRow);
    });
    setDataSource(newDataSource);
  }, [tableHeaders, displayData, dateHeaders, weekTimeline, siteMetadata]);

  /**
   * Build the Harvest-Planner table.
   * If the API returns no data we still create rows for every
   * (greenhouse × variety) that the user has selected, pre-filled
   * with the default / zero values.
   */
  const setHarvestScheduleTable = (bigData) => {
    // Only tomatoes use the Harvest-Planner
    if (currentCrop.name !== "tomato") return;

    //------------------------------------------------------------------
    // 1. Helpers
    //------------------------------------------------------------------
    const dateIndexMap = {};
    dateHeaders.forEach((headerDate, idx) => {
      const iso = dayjs(headerDate).format(BACKEND_DATE_FORMAT);
      dateIndexMap[iso] = idx;
    });

    // Convenience shorthands
    const siteName = currentSite?.displayName;
    const ghList = currentGreenhouse || [];
    const varList = currentVariety || [];

    //------------------------------------------------------------------
    // 2. Build a *full* skeleton table for every GH × Variety combo
    //    selected by the user.  This guarantees the table exists even
    //    when the API returns no harvest-schedule records at all.
    //------------------------------------------------------------------
    const harvestTableTemp = {};

    if (siteName) {
      harvestTableTemp[siteName] = {};

      ghList.forEach((gh) => {
        harvestTableTemp[siteName][gh.name] = {};

        varList.forEach((v) => {
          harvestTableTemp[siteName][gh.name][v.name] = {};

          harvestScheduleMetrics.forEach((metric) => {
            // Pre-fill the 7-day array with the site default or 0
            const defaultVal =
              harvestScheduleMetricsDefaultValues?.[metric] ?? 0;

            harvestTableTemp[siteName][gh.name][v.name][metric] =
              Array(7).fill(defaultVal);
          });
        });
      });
    }

    //------------------------------------------------------------------
    // 3. Overlay any real data that DID come back from the backend.
    //    (If `bigData` is empty this loop is skipped – the skeleton
    //    remains untouched and becomes the data we render.)
    //------------------------------------------------------------------
    for (const siteKey in bigData) {
      const siteDict = bigData[siteKey] || {};
      for (const ghKey in siteDict) {
        const ghDict = siteDict[ghKey] || {};
        for (const varietyKey in ghDict) {
          const varDict = ghDict[varietyKey] || {};

          for (const dateStr in varDict) {
            const dayIdx = dateIndexMap[dateStr];
            if (dayIdx === undefined) continue; // date outside the 7-day window

            const harvestSchedule = varDict[dateStr]?.harvest_schedule || {};

            harvestScheduleMetrics.forEach((metric) => {
              const metricVal =
                harvestSchedule?.[metric] ??
                harvestScheduleMetricsDefaultValues?.[metric] ??
                0;

              harvestTableTemp[siteKey] ??= {};
              harvestTableTemp[siteKey][ghKey] ??= {};
              harvestTableTemp[siteKey][ghKey][varietyKey] ??= {};
              harvestTableTemp[siteKey][ghKey][varietyKey][metric] ??= Array(
                7,
              ).fill(harvestScheduleMetricsDefaultValues?.[metric] ?? 0);

              harvestTableTemp[siteKey][ghKey][varietyKey][metric][dayIdx] =
                metricVal;
            });
          }
        }
      }
    }

    setTableData(harvestTableTemp);
  };

  /**
   * Handle copying the previous week's harvest schedule data (e.g., 7 days prior).
   * We'll call `copyPreviousVarietyHarvestedData` to get older data, then merge it.
   */
  const handleCopyPreviousSetting = async () => {
    try {
      // 1) Gather greenhouse + variety IDs
      const greenhouseIds = currentGreenhouse.map((gh) => gh.id);
      const varietyIds = currentVariety.map((v) => v.id);

      // 2) Gather the current 7 dates from dateHeaders in "YYYY-MM-DD" format
      const dates = dateHeaders.map((d) =>
        dayjs(d).format(BACKEND_DATE_FORMAT),
      );

      // 3) Call the API. copyDateBefore=7 means "copy from 7 days prior"
      const response = await copyPreviousVarietyHarvestedData(
        currentOrg,
        currentSite?.id,
        currentCrop?.id,
        greenhouseIds,
        varietyIds,
        dates,
      );

      if (response?.data?.success) {
        messageApi.open({
          type: "success",
          content: "Copied Previous Setting successfully!",
        });
        fetchAllData();
      } else {
        messageApi.error("Failed to copy previous setting.");
        return;
      }
    } catch (err) {
      console.error("handleCopyPreviousSetting error:", err);
      messageApi.error("Error copying previous settings.");
    }
  };

  // ----------------------------------------------------------------
  // Editing the Harvest Table
  // ----------------------------------------------------------------
  const handleHarvestCellValueChange = (record, dayIndex, newValue) => {
    const dayNum = parseInt(dayIndex.replace("day", ""), 10);
    if (Number.isNaN(dayNum)) return;

    const { site, greenhouse, variety, metricKey } = record;
    const newDisplayData = structuredClone(displayData);
    newDisplayData[site][greenhouse][variety][metricKey][dayNum] = newValue;
    setDisplayData(newDisplayData);
  };

  // Construct the nested request body for the backend
  const constructBackendHarvestedDataBody = (tableData) => {
    const requestBody = {};
    currentGreenhouse.forEach((gh) => {
      const ghKey = gh.name;
      if (!requestBody[ghKey]) {
        requestBody[ghKey] = {};
      }
      currentVariety.forEach((variety) => {
        const varietyKey = variety.name;
        if (!requestBody[ghKey][varietyKey]) {
          requestBody[ghKey][varietyKey] = {};
        }
        dateHeaders.forEach((dateStr, dayIndex) => {
          const isoDate = dayjs(dateStr).format(BACKEND_DATE_FORMAT);
          const dayMetrics = {};
          // Access siteKey by currentSite.displayName
          const siteKey = currentSite.displayName;
          if (
            tableData[siteKey] &&
            tableData[siteKey][ghKey] &&
            tableData[siteKey][ghKey][varietyKey]
          ) {
            for (const metricKey in tableData[siteKey][ghKey][varietyKey]) {
              const dayValues =
                tableData[siteKey][ghKey][varietyKey][metricKey];
              dayMetrics[metricKey] = dayValues?.[dayIndex] ?? 0;
            }
          }
          requestBody[ghKey][varietyKey][isoDate] = dayMetrics;
        });
      });
    });
    return requestBody;
  };

  const handleHarvestUpdate = async () => {
    const newTable = structuredClone(tableData);

    // Copy over user edits from displayData
    for (const siteKey in displayData) {
      for (const ghKey in displayData[siteKey]) {
        for (const varietyKey in displayData[siteKey][ghKey]) {
          for (const metricKey in displayData[siteKey][ghKey][varietyKey]) {
            const arrValues =
              displayData[siteKey][ghKey][varietyKey][metricKey];
            if (!newTable[siteKey]) newTable[siteKey] = {};
            if (!newTable[siteKey][ghKey]) newTable[siteKey][ghKey] = {};
            if (!newTable[siteKey][ghKey][varietyKey]) {
              newTable[siteKey][ghKey][varietyKey] = {};
            }
            newTable[siteKey][ghKey][varietyKey][metricKey] = [...arrValues];
          }
        }
      }
    }

    // Construct payload
    let payload = constructBackendHarvestedDataBody(newTable);

    try {
      let response = await UpdateVarietyHarvestedData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        payload,
      );
      if (response.status === 200) {
        setTableData(newTable);
        await fetchAllData();
        messageApi.open({
          type: "success",
          content: "Harvest data updated successfully!",
        });
      } else {
        messageApi.open({
          type: "error",
          content: "Harvest data failed to save",
        });
      }
    } catch (err) {
      console.error("UpdateVarietyHarvestedData error:", err);
      messageApi.open({
        type: "error",
        content: "Error saving harvest data!",
      });
    }
  };

  // ----------------------------------------------------------------
  // Build displayAnalysisData from analysisTableData
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!dateHeaders.length) return;

    const newDisplay = {};
    for (const siteKey in analysisTableData) {
      newDisplay[siteKey] = {};
      for (const ghKey in analysisTableData[siteKey]) {
        newDisplay[siteKey][ghKey] = {};
        for (const varietyKey in analysisTableData[siteKey][ghKey]) {
          const { YieldByDate, RefinedByDate } =
            analysisTableData[siteKey][ghKey][varietyKey];

          const yieldArr = dateHeaders.map((dh) => {
            const iso = dayjs(dh).format("YYYY-MM-DD");
            return YieldByDate?.[iso] ?? null;
          });
          const refinedArr = dateHeaders.map((dh) => {
            const iso = dayjs(dh).format("YYYY-MM-DD");
            return RefinedByDate?.[iso] ?? null;
          });

          // Get Actual Yield from Harvest Table
          const actualYieldArr = dateHeaders.map((dh, idx) => {
            const actualYield =
              tableData[siteKey]?.[ghKey]?.[varietyKey]?.[
                "Actual Yield (Kg)"
              ]?.[idx];
            if (
              actualYield === null ||
              actualYield === undefined ||
              actualYield === ""
            ) {
              return "N/A";
            }
            return actualYield;
          });

          newDisplay[siteKey][ghKey][varietyKey] = {
            "Forecast Yield (Kg)": yieldArr,
            "Refined Yield (Kg)": refinedArr,
            "Actual Yield (Kg)": actualYieldArr,
          };
        }
      }
    }
    setDisplayAnalysisData(newDisplay);
  }, [analysisTableData, tableData, dateHeaders]);

  // ----------------------------------------------------------------
  // Build Analysis dataSource
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!tableHeaders.length || !displayAnalysisData || !dateHeaders.length)
      return;

    const newDataSource = [];
    const varietyGroups = {};

    tableHeaders.forEach(({ site, greenhouse, variety }) => {
      const varietyKey = `${site}-${variety}`;
      if (!varietyGroups[varietyKey]) {
        varietyGroups[varietyKey] = { site, variety, greenhouses: [] };
      }
      varietyGroups[varietyKey].greenhouses.push(greenhouse);
    });

    const summaryAverageYieldKey =
      currentSite?.metadata?.[currentCrop?.name]?.forecast
        ?.SUMMARY_AVERAGE_YIELD_KEY;
    const refinedAverageYieldKey =
      currentSite?.metadata?.[currentCrop?.name]?.forecast
        ?.REFINED_AVERAGE_YIELD_KEY;
    const averageYieldKey =
      currentSite?.metadata?.[currentCrop?.name]?.forecast?.AVERAGE_YIELD_KEY;

    let summaryPref =
      summaryAverageYieldKey || refinedAverageYieldKey || averageYieldKey;
    const summaryMetricKey =
      summaryPref && summaryPref === refinedAverageYieldKey
        ? "Refined Yield"
        : "Yield";

    for (let varietyKey in varietyGroups) {
      const { site, variety, greenhouses } = varietyGroups[varietyKey];
      const isLoading = varietyLoadingMap[varietyKey];
      const parentKey = `analysis-${varietyKey}-parent`;

      // If still loading
      if (isLoading) {
        newDataSource.push({
          key: parentKey,
          isParent: true,
          isLoading: true,
          subsite: variety,
          detail: "",
          children: [],
        });
        continue;
      }

      // We'll store the daily sums for the summary row in sumArr
      const sumArr = Array(dateHeaders.length).fill(0);

      greenhouses.forEach((gh) => {
        const varietyObj = displayAnalysisData[site]?.[gh]?.[variety];
        if (!varietyObj) return;

        // We'll get the arrays for each metric
        const summaryArr = varietyObj[summaryMetricKey] || [];
        const refinedArr = varietyObj["Refined Yield"] || [];
        const yieldArr = varietyObj["Yield"] || [];

        for (let i = 0; i < dateHeaders.length; i++) {
          let dayVal = null;

          const maybeSummaryVal = summaryArr[i];
          if (summaryAverageYieldKey && maybeSummaryVal != null) {
            if (maybeSummaryVal !== "N/A") {
              dayVal = maybeSummaryVal;
            }
          }

          // If dayVal still null, fallback
          if (dayVal == null) {
            // Check refined
            const refinedVal = refinedArr[i];
            const yieldVal = yieldArr[i];

            // If we have a non-null, non-"N/A" refined => use it
            if (refinedVal != null && refinedVal !== "N/A") {
              dayVal = refinedVal;
            }
            // Else if yield is non-null => use it
            else if (yieldVal != null && yieldVal !== "N/A") {
              dayVal = yieldVal;
            }
            // Else 0
            else {
              dayVal = 0;
            }
          }

          // If it's a berry crop and dayVal is 0 => stable random
          if (isBerry && dayVal === 0) {
            dayVal = stableRandomFourDigit(
              site,
              gh,
              variety,
              dateHeaders[i],
              "SummaryFallback",
            );
          }

          let numericVal =
            typeof dayVal === "number" ? dayVal : parseFloat(dayVal) || 0;
          sumArr[i] += numericVal;
        }
      });

      // Build a summary row
      const FROM_WEEK =
        weekTimeline[greenhouses[0]]?.[variety]?.FROM_WEEK ?? null;
      const FROM_YEAR =
        weekTimeline[greenhouses[0]]?.[variety]?.FROM_YEAR ?? null;
      const TO_WEEK = weekTimeline[greenhouses[0]]?.[variety]?.TO_WEEK ?? null;
      const TO_YEAR = weekTimeline[greenhouses[0]]?.[variety]?.TO_YEAR ?? null;

      const summaryRow = {
        key: `analysis-summary-${varietyKey}`,
        isParent: false,
        isSummary: true,
        subsite: `Summary ${variety}`,
        // detail: metricKeys.join("<br>"),
        detail: "",
      };
      dateHeaders.forEach((_, dayIndex) => {
        summaryRow[`day${dayIndex}`] = sumArr[dayIndex];
      });

      // Build greenhouse child rows
      const greenhouseRows = [];
      greenhouses.forEach((gh) => {
        const varietyObj = displayAnalysisData[site]?.[gh]?.[variety];
        if (!varietyObj) return;
        const ghMetricKeys = Object.keys(varietyObj);

        const ghRow = {
          key: `analysis-${site}-${gh}-${variety}`,
          isParent: false,
          isSummary: false,
          subsite: `${site} - ${gh} ${variety}<br>W ${FROM_WEEK},${FROM_YEAR} - ${TO_WEEK},${TO_YEAR}`,
          detail: ghMetricKeys.join("<br>"),
        };

        for (let d = 0; d < dateHeaders.length; d++) {
          // For each metric (Yield, Refined Yield), handle 0 => random if berry
          let dayVals = ghMetricKeys.map((mKey) => {
            let val = varietyObj[mKey][d];
            if (isBerry && val === 0) {
              val = stableRandomFourDigit(
                site,
                gh,
                variety,
                dateHeaders[d],
                mKey,
              );
            }
            if (val == null) val = "N/A";
            return val;
          });

          ghRow[`day${d}`] = dayVals.join("<br>");
        }
        greenhouseRows.push(ghRow);
      });

      const parentRow = {
        key: parentKey,
        isParent: true,
        isLoading: false,
        subsite: variety,
        detail: "",
        children: [summaryRow, ...greenhouseRows],
      };
      newDataSource.push(parentRow);
    }

    setAnalysisDataSource(newDataSource);

    // Possibly show/hide the "Metrics" column if there's only 1 metric
    const distinctMetrics = new Set();
    function gatherMetrics(row) {
      if (row.detail) {
        row.detail.split("<br>").forEach((m) => distinctMetrics.add(m.trim()));
      }
      if (row.children) {
        row.children.forEach((c) => gatherMetrics(c));
      }
    }
    newDataSource.forEach((r) => gatherMetrics(r));
    setShowMetricColumn(distinctMetrics.size > 1);
  }, [
    tableHeaders,
    displayAnalysisData,
    dateHeaders,
    weekTimeline,
    varietyLoadingMap,
    isBerry,
    currentSite,
    currentCrop,
  ]);

  // ----------------------------------------------------------------
  // Expand handling
  // ----------------------------------------------------------------
  const handleExpand = (expanded, record) => {
    if (expanded) {
      setExpandedKeys((prev) => [...prev, record.key]);
    } else {
      setExpandedKeys((prev) => prev.filter((k) => k !== record.key));
    }
  };

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <div id="dashboard">
      <Sidebar
        active="home"
        sidebarType="TomatoGrower"
        open={sidebarOpen}
        setOpen={setSidebarOpen}
      />
      {contextHolder}
      <RipenessChart
        isOpen={ripenessModalOpen}
        onClose={() => setRipenessModalOpen(false)}
        siteMetadata={siteMetadata}
        varietyData={varietyData}
      />
      <div
        className={`flex flex-col w-full px-20 ${
          sidebarOpen ? "pl-60" : ""
        } pt-10 md:pt-4 pb-14 md:pl-15 md:pr-2 sm:pr-10 justify-center transition-padding ease-in-out duration-200`}
      >
        <Dropdowns
          siteMetadata={siteMetadata}
          setSiteMetadata={setSiteMetadata}
          currentMetric={currentMetric}
          setCurrentMetric={setCurrentMetric}
          setMetricType={setMetricType}
          metricType={metricType}
          currentOrg={currentOrg}
          setCurrentOrg={setCurrentOrg}
          orgData={orgData}
          setOrgData={setOrgData}
          orgLoading={orgLoading}
          setOrgLoading={setOrgLoading}
          currentSite={currentSite}
          setCurrentSite={setCurrentSite}
          siteData={siteData}
          setSiteData={setSiteData}
          siteLoading={siteLoading}
          setSiteLoading={setSiteLoading}
          currentCrop={currentCrop}
          setCurrentCrop={setCurrentCrop}
          cropData={cropData}
          setCropData={setCropData}
          cropLoading={cropLoading}
          setCropLoading={setCropLoading}
          currentGreenhouse={currentGreenhouse}
          setCurrentGreenhouse={setCurrentGreenhouse}
          greenhouseData={greenhouseData}
          setGreenhouseData={setGreenhouseData}
          currentVariety={currentVariety}
          setCurrentVariety={setCurrentVariety}
          varietyData={varietyData}
          setVarietyData={setVarietyData}
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          setMetricData={setMetricData}
          metricData={metricData}
          setDropdownLoading={setDropdownLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          settingDefaults={settingDefaults}
          setSettingDefaults={setSettingDefaults}
          disabledDate={disabledDate}
        />

        {errorMessage && !dropdownLoading ? (
          <div className="text-2xl text-center">{errorMessage}</div>
        ) : (
          infoMessage &&
          !dropdownLoading && (
            <div className="text-2xl text-center">{infoMessage}</div>
          )
        )}

        {/* Weekly Budget + Buttons Row (only for berries) */}
        {isBerry ? (
          <div className="text-xl font-semibold flex flex-row sm:flex-col justify-between items-center mt-4 mb-4 gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-primaryBlue">
              <span>Weekly Budget:</span>
              <InputNumber min={0} placeholder="0" className="w-20" />
              <span>punnets</span>
            </div>
            <div className="flex flex-row sm:flex-col justify-end items-center gap-4 text-primaryBlue">
              <Tooltip title="Feature Not Implemented">
                <Button
                  className="sm:w-full w-48 bg-white rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
                  disabled
                  variant="outlined"
                  color="primary"
                >
                  Copy Previous Setting
                </Button>
              </Tooltip>

              <Button
                className="sm:w-full w-48 bg-white rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
                onClick={handleHarvestUpdate}
                variant="outlined"
                color="primary"
              >
                Update
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-row sm:flex-col justify-between items-center mt-4 mb-4 gap-4">
            <Button
              className="sm:w-full w-48 bg-white rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
              onClick={() => setRipenessModalOpen(true)}
              variant="outlined"
              color="primary"
            >
              Ripeness Chart
            </Button>

            <div className="flex flex-row sm:flex-col gap-4 sm:w-full">
              <Button
                className="sm:w-full w-48 bg-white rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
                variant="outlined"
                color="primary"
                onClick={handleCopyPreviousSetting}
              >
                Copy Previous Setting
              </Button>

              <Button
                className="sm:w-full w-48 bg-white rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105"
                onClick={handleHarvestUpdate}
                variant="outlined"
                color="primary"
              >
                Update
              </Button>
            </div>
          </div>
        )}

        {/* HARVEST TABLE */}
        <h2 className="text-xl font-semibold mt-8 mb-4 text-primaryBlue">
          Harvest Planner
        </h2>
        <div
          style={{ overflowX: "auto", marginBottom: "2rem" }}
          className="bg-white rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100"
        >
          <HarvestTable
            dateHeaders={dateHeaders}
            dataSource={dataSource}
            onCellValueChange={handleHarvestCellValueChange}
            siteMetadata={siteMetadata}
          />
        </div>

        {/* ANALYSIS TABLE */}
        <h2 className="text-xl font-semibold mt-8 mb-4 text-primaryBlue">
          Yield Forecast
        </h2>
        <div
          style={{ overflowX: "auto" }}
          className="bg-white rounded-lg shadow-xl hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100"
        >
          <AnalysisTable
            dateHeaders={dateHeaders}
            analysisDataSource={analysisDataSource}
            expandedKeys={expandedKeys}
            onExpand={handleExpand}
            showMetricColumn={showMetricColumn}
          />
        </div>
      </div>
      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
};

export default TomatoGrowerDashboard;
